import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import { toast } from "sonner";
import QuickReplyChips from "../components/QuickReplyChips";
import {
  ChevronLeft, MessageCircle, Upload, Camera, CheckCircle2, AlertTriangle,
  Clock, Loader2, Trophy, Image as ImageIcon, Send, Info, Lock, ShieldCheck,
  CalendarClock, X,
} from "lucide-react";

/**
 * V2 / Mobile-native Match Detail.
 * Renders inside <MobileShell> at /v2/matches/:id so the bottom nav and dark
 * mobile chrome stay visible — players never bounce into the desktop V1 UI.
 *
 * Reuses the existing V1 endpoints unchanged:
 *   GET  /matches/:id
 *   POST /matches/:id/submit (multipart: home_score, away_score, screenshot)
 */
export default function MatchDetailV2() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useT();

  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  // Scores are entered from the viewer's perspective (your score / opponent
  // score). We translate to home/away when calling the API.
  const [myScore, setMyScore] = useState(0);
  const [oppScore, setOppScore] = useState(0);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();
  // Sibling leg discovered via /h2h/:opponentId so we can render the
  // Game 1 / Game 2 tabs and enforce the Game-2-locked-until-Game-1 rule.
  const [pair, setPair] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/matches/${id}`);
      setMatch(data);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  // Once we know who the opponent is, fetch both legs of the pair so we can
  // render Game 1 / Game 2 tabs. Same-day matchday means /h2h returns both.
  useEffect(() => {
    if (!match || !user?.id) return;
    const oppId = match.home_id === user.id ? match.away_id
      : match.away_id === user.id ? match.home_id
      : null;
    if (!oppId) { setPair(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/h2h/${oppId}`);
        if (cancelled || !data) return;
        // Filter to the same matchday as the currently-viewed match so we
        // don't accidentally surface legs from previous rounds.
        const sameDay = (data.matches || []).filter(
          (m) => (m.scheduled_at || "") === (match.scheduled_at || "")
        );
        setPair(sameDay.length >= 2 ? sameDay.sort((a, b) => (a.order || 1) - (b.order || 1)) : null);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [match, user?.id]);

  // Mod role check is also used as a feature gate for the override panel.
  const isModOrAdmin = user?.role === "admin" || user?.role === "moderator";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-[#52525B]" data-testid="v2-match-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (!match) {
    return (
      <div className="px-5 pt-5">
        <BackBar onBack={() => nav(-1)} />
        <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-6 text-center text-[#71717A] text-sm" data-testid="v2-match-notfound">
          {t("v2.match.notfound")}
        </div>
      </div>
    );
  }

  const isHome = user?.id === match.home_id;
  const isAway = user?.id === match.away_id;
  const mySubmission = isHome ? match.home_submission : isAway ? match.away_submission : null;

  // Game-2-locked-until-Game-1 rule: pair[0] = game1, pair[1] = game2.
  const currentOrder = match.order || 1;
  const game1 = pair?.[0] || null;
  const game1Finalized = !game1 || game1.status === "confirmed" || game1.admin_override;
  const locked = currentOrder === 2 && !game1Finalized;

  const canSubmit = (isHome || isAway) && !mySubmission && match.status !== "confirmed" && !match.admin_override && !locked;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error(t("v2.match.toast.noFile")); return; }
    setSubmitting(true);
    try {
      // Map perspective scores back to home/away so the backend contract
      // doesn't change. If viewer is "home", my=home, opp=away.
      const homeScore = isHome ? myScore : oppScore;
      const awayScore = isHome ? oppScore : myScore;
      const form = new FormData();
      form.append("home_score", String(homeScore));
      form.append("away_score", String(awayScore));
      form.append("screenshot", file);
      await api.post(`/matches/${id}/submit`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("v2.match.toast.submitted"));
      setFile(null); if (fileRef.current) fileRef.current.value = "";
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const home = match.home || {};
  const away = match.away || {};
  const opponent = isHome ? away : isAway ? home : null;

  return (
    <div className="px-5 pt-5 pb-6 space-y-4" data-testid="v2-match-detail">
      <BackBar onBack={() => nav(-1)} />

      {/* Game 1 / Game 2 leg tabs (only shown when the H2H pair exists, i.e.
          two same-day legs were found for these two opponents). */}
      {pair && pair.length === 2 && (
        <div
          className="flex gap-1 p-1 bg-[#0F0F18] rounded-2xl border border-[#1F1F2A]"
          data-testid="v2-match-leg-tabs"
        >
          {pair.map((leg, idx) => {
            const isActive = leg.id === id;
            const legFinalized = leg.status === "confirmed" || leg.admin_override;
            const legLocked = idx === 1 && pair[0].status !== "confirmed" && !pair[0].admin_override;
            return (
              <button
                key={leg.id}
                type="button"
                onClick={() => !legLocked && nav(`/v2/matches/${leg.id}`, { replace: true })}
                disabled={legLocked && !isActive}
                data-testid={`v2-match-leg-tab-${idx + 1}`}
                className={`flex-1 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest transition-all inline-flex items-center justify-center gap-1.5 ${
                  isActive
                    ? "bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white shadow-lg shadow-[#7C3AED]/20"
                    : legLocked
                      ? "text-[#52525B] cursor-not-allowed"
                      : "text-[#71717A] hover:text-white"
                }`}
              >
                {legLocked && <Lock className="w-3 h-3" />}
                {legFinalized && !legLocked && <CheckCircle2 className="w-3 h-3" />}
                {t("v2.h2h.game", { n: idx + 1 })}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#A5B4FC]">
          {match.round != null ? t("v2.my.round", { n: match.round + 1 }) : t("v2.match.scheduled")}
        </span>
        <span className="text-[10px] text-[#52525B]">·</span>
        <span className="text-[10px] font-mono text-[#71717A]">{match.scheduled_at || "—"}</span>
        <span className="ml-auto"><StatusPill status={match.status} adminOverride={match.admin_override} /></span>
      </div>

      {/* Score hero */}
      <section className="rounded-3xl bg-gradient-to-br from-[#1F1F2A] via-[#12121C] to-[#0A0A14] border border-[#27272F] p-5 shadow-2xl shadow-black/40" data-testid="v2-match-hero">
        <div className="grid grid-cols-3 items-center gap-3">
          <PlayerCol player={home} side="home" highlight={isHome} t={t} />
          <div className="text-center">
            <div className="text-4xl font-bold text-white tabular-nums">
              {match.confirmed_score_home ?? "·"}
              <span className="mx-2 text-[#52525B]">–</span>
              {match.confirmed_score_away ?? "·"}
            </div>
            <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.25em] text-[#71717A]">
              {match.status === "confirmed" ? t("v2.match.final") : t("v2.match.pending")}
            </div>
          </div>
          <PlayerCol player={away} side="away" highlight={isAway} t={t} />
        </div>

        {match.auto_drawn && (
          <div className="mt-3 text-center text-[10px] font-mono uppercase tracking-[0.25em] text-[#F59E0B]">
            {t("v2.match.autoDrawn")}
          </div>
        )}
        {match.admin_override && (
          <div className="mt-3 text-center text-[10px] font-mono uppercase tracking-[0.25em] text-[#7C3AED]">
            {t("v2.match.adminOverride")}
          </div>
        )}
      </section>

      {/* Submissions side-by-side */}
      <section className="grid grid-cols-1 gap-3" data-testid="v2-match-submissions">
        <SubmissionCard
          label={t("v2.match.home")}
          submission={match.home_submission}
          player={home}
          isMe={isHome}
          t={t}
        />
        <SubmissionCard
          label={t("v2.match.away")}
          submission={match.away_submission}
          player={away}
          isMe={isAway}
          t={t}
        />
      </section>

      {/* Game 2 locked notice — only when current leg is order=2 and game 1
          is still in flight. Replaces the submit form so players can't
          accidentally submit a result for a leg they can't legally play yet. */}
      {locked && (isHome || isAway) && (
        <section
          className="rounded-2xl bg-[#0F0F18] border border-dashed border-[#1F1F2A] p-5 text-center"
          data-testid="v2-match-locked"
        >
          <Lock className="w-7 h-7 text-[#52525B] mx-auto mb-2" />
          <h3 className="text-sm font-bold text-white">{t("v2.match.locked.title")}</h3>
          <p className="text-xs text-[#A1A1AA] mt-1 max-w-xs mx-auto leading-relaxed">{t("v2.match.locked.body")}</p>
          {game1 && (
            <button
              type="button"
              onClick={() => nav(`/v2/matches/${game1.id}`, { replace: true })}
              data-testid="v2-match-locked-goto-g1"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#7C3AED]/15 border border-[#7C3AED]/40 text-[#A5B4FC] px-4 py-2 text-[11px] font-bold uppercase tracking-widest hover:bg-[#7C3AED]/25 transition-colors"
            >
              {t("v2.match.locked.gotoG1")}
            </button>
          )}
        </section>
      )}

      {/* Submit form (only if eligible) */}
      {canSubmit && (
        <section className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-4 space-y-3" data-testid="v2-match-submit-form">
          <div className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-[#A5B4FC]" />
            <h3 className="text-sm font-bold text-white">{t("v2.match.submit.title")}</h3>
          </div>
          <p className="text-xs text-[#A1A1AA]">{t("v2.match.submit.help")}</p>

          <form onSubmit={onSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <ScoreInput
                label={t("v2.match.yourScore")}
                value={myScore}
                onChange={setMyScore}
                testId="v2-match-score-me"
                accent="#10B981"
              />
              <ScoreInput
                label={t("v2.match.oppScore")}
                value={oppScore}
                onChange={setOppScore}
                testId="v2-match-score-opp"
              />
            </div>

            <label
              htmlFor="v2-match-file"
              className="flex items-center justify-between rounded-xl bg-[#0F0F18] border border-dashed border-[#27272F] hover:border-[#7C3AED] px-4 py-3 cursor-pointer transition-colors"
              data-testid="v2-match-file-label"
            >
              <span className="flex items-center gap-2 min-w-0">
                <Camera className="w-4 h-4 text-[#A5B4FC] flex-shrink-0" />
                <span className="text-xs text-white truncate">
                  {file ? file.name : t("v2.match.submit.screenshot")}
                </span>
              </span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#71717A] flex-shrink-0">
                {file ? t("v2.match.submit.change") : t("v2.match.submit.pick")}
              </span>
            </label>
            <input
              id="v2-match-file"
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="v2-match-file-input"
            />

            <button
              type="submit"
              disabled={submitting || !file}
              data-testid="v2-match-submit-btn"
              className="w-full rounded-2xl bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white font-bold text-sm py-3.5 shadow-lg shadow-[#7C3AED]/30 hover:opacity-95 disabled:opacity-50 transition-opacity inline-flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {submitting ? t("v2.match.submit.busy") : t("v2.match.submit.cta")}
            </button>
          </form>
        </section>
      )}

      {/* Reschedule controls — mutual-approval flow. Available while the
          fixture is not yet confirmed/admin-overridden. Reuses
          POST /matches/:id/reschedule and DELETE /matches/:id/reschedule
          which were already implemented in V1. */}
      {(isHome || isAway) && match.status !== "confirmed" && !match.admin_override && (
        <ReschedulePanel match={match} userId={user?.id} onChanged={refresh} t={t} />
      )}

      {/* Match-day pair chat (only for the two players, only when match is
          still in flight). Triggers chat-silence forfeit logic on the backend
          if exactly one player messages on match day. */}
      {(isHome || isAway) && match.status !== "confirmed" && !match.admin_override && (
        <MatchChat matchId={id} userId={user?.id} opponent={isHome ? away : home} t={t} />
      )}

      {match.status === "disputed" && (
        <div className="rounded-2xl bg-[#E11D48]/10 border border-[#E11D48]/40 p-4 text-sm text-[#FCA5A5]" data-testid="v2-match-disputed">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4" />
            <strong>{t("v2.match.disputed.title")}</strong>
          </div>
          <p className="text-xs">{t("v2.match.disputed.body")}</p>
        </div>
      )}

      {/* Mod / Admin override card — lets staff manually set the final score
          on disputed games or any match a player reports. Reuses unchanged
          POST /admin/matches/:id/override. Hidden for non-staff. */}
      {isModOrAdmin && (
        <ModOverrideCard
          match={match}
          home={home}
          away={away}
          onApplied={refresh}
          t={t}
        />
      )}
    </div>
  );
}

function BackBar({ onBack }) {
  const { t } = useT();
  return (
    <button
      type="button"
      onClick={onBack}
      data-testid="v2-match-back"
      className="inline-flex items-center gap-1.5 text-[#A1A1AA] hover:text-white text-xs font-mono uppercase tracking-[0.25em] transition-colors"
    >
      <ChevronLeft className="w-4 h-4" />
      {t("v2.match.back")}
    </button>
  );
}

function PlayerCol({ player, side, highlight, t }) {
  const name = player?.username || "—";
  const whatsapp = player?.whatsapp;
  return (
    <div className="text-center">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#71717A]">
        {side === "home" ? t("v2.match.home") : t("v2.match.away")}
      </div>
      <Link
        to={player?.username ? `/v2/players/${encodeURIComponent(player.username)}` : "#"}
        className={`mt-1 inline-block max-w-full ${highlight ? "text-[#10B981]" : "text-white"} font-bold text-sm truncate`}
        data-testid={`v2-match-${side}-name`}
      >
        {name}
        {highlight && <span className="ml-1 text-[9px] uppercase tracking-widest text-[#10B981]">·{t("v2.match.you")}</span>}
      </Link>
      {player?.game_id && (
        <div className="text-[9px] font-mono text-[#52525B] truncate">{player.game_id}</div>
      )}
      {whatsapp && (
        <a
          href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`v2-match-${side}-whatsapp`}
          className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 text-[#25D366] text-[9px] font-mono uppercase tracking-widest hover:bg-[#25D366]/25 transition-colors"
        >
          <MessageCircle className="w-2.5 h-2.5" /> WA
        </a>
      )}
    </div>
  );
}

function ScoreInput({ label, value, onChange, testId, accent }) {
  return (
    <div>
      <div
        className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1.5"
        style={{ color: accent || "#71717A" }}
      >{label}</div>
      <input
        type="number"
        min={0}
        max={50}
        value={value}
        onChange={(e) => onChange(Math.max(0, Math.min(50, parseInt(e.target.value || "0", 10))))}
        data-testid={testId}
        className="w-full rounded-xl bg-[#0F0F18] border border-[#1F1F2A] focus:border-[#7C3AED] focus:outline-none px-4 py-3 text-2xl text-center font-bold text-white tabular-nums transition-colors"
      />
    </div>
  );
}

function StatusPill({ status, adminOverride }) {
  const { t } = useT();
  if (adminOverride) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#7C3AED]/15 border border-[#7C3AED]/40 text-[#A5B4FC] text-[9px] font-bold uppercase tracking-widest" data-testid="v2-match-status">
        {t("v2.match.status.admin")}
      </span>
    );
  }
  const map = {
    confirmed: { Icon: CheckCircle2, color: "#10B981", label: t("v2.match.status.confirmed") },
    pending:   { Icon: Clock,        color: "#F59E0B", label: t("v2.match.status.pending") },
    disputed:  { Icon: AlertTriangle,color: "#E11D48", label: t("v2.match.status.disputed") },
    scheduled: { Icon: Clock,        color: "#A1A1AA", label: t("v2.match.status.scheduled") },
  };
  const cfg = map[status] || map.scheduled;
  const Icon = cfg.Icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest"
      style={{ backgroundColor: `${cfg.color}22`, color: cfg.color, border: `1px solid ${cfg.color}55` }}
      data-testid="v2-match-status"
    >
      <Icon className="w-2.5 h-2.5" />
      {cfg.label}
    </span>
  );
}

function SubmissionCard({ label, submission, player, isMe, t }) {
  const received = !!submission;
  const accent = received ? "#10B981" : "#52525B";
  return (
    <div
      className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-4"
      data-testid={`v2-match-submission-${label.toLowerCase()}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Trophy className="w-3.5 h-3.5 text-[#A5B4FC] flex-shrink-0" />
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#71717A] truncate">
            {label} {isMe && <span className="text-[#10B981]">· {t("v2.match.you")}</span>}
          </div>
        </div>
        <span
          className="text-[9px] font-bold uppercase tracking-widest"
          style={{ color: accent }}
          data-testid={`v2-match-submission-state-${label.toLowerCase()}`}
        >
          {received ? t("v2.match.submission.received") : t("v2.match.submission.waiting")}
        </span>
      </div>
      <div className="text-sm font-bold text-white truncate">{player?.username || "—"}</div>
      {received ? (
        <>
          <div className="text-2xl font-bold text-white mt-1 tabular-nums">
            {submission.score_home}<span className="text-[#52525B] mx-1.5">–</span>{submission.score_away}
          </div>
          {submission.screenshot_path && (
            <SubmissionScreenshot path={submission.screenshot_path} />
          )}
        </>
      ) : (
        <div className="mt-2 text-[11px] font-mono text-[#52525B] uppercase tracking-widest">
          {t("v2.match.submission.empty")}
        </div>
      )}
    </div>
  );
}

function SubmissionScreenshot({ path }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get(`/files/${path}`, { responseType: "blob" });
        if (!cancelled) setUrl(URL.createObjectURL(resp.data));
      } catch (e) {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line
  }, [path]);

  if (err) {
    return (
      <div className="mt-2 rounded-xl bg-[#0F0F18] border border-dashed border-[#1F1F2A] px-3 py-2 text-[10px] font-mono text-[#71717A] flex items-center gap-1.5">
        <ImageIcon className="w-3 h-3" /> screenshot unavailable
      </div>
    );
  }
  if (!url) {
    return (
      <div className="mt-2 h-32 rounded-xl bg-[#0F0F18] border border-[#1F1F2A] flex items-center justify-center">
        <Loader2 className="w-4 h-4 text-[#52525B] animate-spin" />
      </div>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block mt-2 rounded-xl overflow-hidden border border-[#1F1F2A] bg-[#0A0A14]"
      data-testid="v2-match-screenshot"
    >
      <img src={url} alt="proof" className="w-full max-h-72 object-contain" />
    </a>
  );
}


/**
 * Pair chat embedded in the V2 match detail page. Reuses the existing
 *   GET  /matches/:id/chat
 *   POST /matches/:id/chat
 * endpoints which already power the chat-silence forfeit logic on the
 * backend. If exactly one player messages here on match day, the day-end
 * sweep awards a 3-0 forfeit to the messaging player and credits +3 to
 * standings, with a "✅ Result corrected — you won!" notification.
 */
function MatchChat({ matchId, userId, opponent, t }) {
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/matches/${matchId}/chat`);
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) { /* noop — silent retry next tick */ }
  }, [matchId]);

  useEffect(() => {
    fetchMessages();
    const iv = setInterval(fetchMessages, 5000);
    return () => clearInterval(iv);
  }, [fetchMessages]);

  // Auto-scroll to latest message (skips the very first paint so users
  // landing on the page aren't snapped to the bottom unexpectedly).
  const lastIdRef = useRef(null);
  useEffect(() => {
    if (!messages || !messages.length) return;
    const lastId = messages[messages.length - 1].id;
    if (lastIdRef.current !== null && lastIdRef.current !== lastId) {
      endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
    lastIdRef.current = lastId;
  }, [messages]);

  const sendText = async (txt) => {
    if (!txt || !txt.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/matches/${matchId}/chat`, { text: txt.trim() });
      setMessages((prev) => [...(prev || []), data]);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 50);
    } catch (e2) {
      toast.error(formatApiError(e2));
      throw e2;
    } finally {
      setSending(false);
    }
  };

  const onSend = async (e) => {
    e.preventDefault();
    const txt = text.trim();
    if (!txt) return;
    try {
      await sendText(txt);
      setText("");
    } catch { /* error already toasted */ }
  };

  const oppName = opponent?.username || t("v2.match.away");

  return (
    <section
      className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] overflow-hidden"
      data-testid="v2-match-chat"
    >
      {/* Header */}
      <div className="px-4 py-3 bg-[#0F0F18] border-b border-[#1F1F2A] flex items-center gap-2">
        <MessageCircle className="w-4 h-4 text-[#A5B4FC]" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-white truncate">{t("v2.match.chat.title", { name: oppName })}</h3>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#71717A]">{t("v2.match.chat.kicker")}</div>
        </div>
      </div>

      {/* Forfeit-rule notice */}
      <div className="px-4 py-2.5 bg-[#7C3AED]/8 border-b border-[#7C3AED]/20 flex items-start gap-2" data-testid="v2-match-chat-rule">
        <Info className="w-3.5 h-3.5 text-[#A5B4FC] flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-[#A5B4FC] leading-relaxed">{t("v2.match.chat.rule")}</p>
      </div>

      {/* Messages */}
      <div
        className="px-3 py-3 max-h-[280px] min-h-[140px] overflow-y-auto space-y-2 bg-[#0A0A14]"
        data-testid="v2-match-chat-list"
      >
        {messages === null ? (
          <div className="flex items-center justify-center py-6 text-[#52525B]">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center text-[#71717A]" data-testid="v2-match-chat-empty">
            <MessageCircle className="w-6 h-6 mb-1.5 text-[#52525B]" />
            <div className="text-xs">{t("v2.match.chat.empty.title")}</div>
            <div className="text-[10px] text-[#52525B] mt-0.5 max-w-xs">{t("v2.match.chat.empty.body")}</div>
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.user_id === userId;
            return (
              <div
                key={m.id}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
                data-testid={`v2-match-chat-msg-${m.id}`}
              >
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                  mine
                    ? "bg-gradient-to-br from-[#7C3AED] to-[#6D28D9] text-white"
                    : "bg-[#12121C] border border-[#1F1F2A] text-white"
                }`}>
                  {!mine && (
                    <div className="text-[9px] font-bold uppercase tracking-widest text-[#A5B4FC] mb-0.5">
                      {m.username || oppName}
                    </div>
                  )}
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                  <div className={`text-[9px] font-mono mt-1 ${mine ? "text-white/60" : "text-[#71717A]"}`}>
                    {fmtChatTs(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <form
        onSubmit={onSend}
        className="px-3 py-2.5 border-t border-[#1F1F2A] bg-[#070710] space-y-2"
        data-testid="v2-match-chat-form"
      >
        <QuickReplyChips
          onSend={sendText}
          disabled={sending}
          testIdPrefix="v2-match-chat-quick"
        />
        <div className="flex gap-2">
          <input
            data-testid="v2-match-chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("v2.match.chat.placeholder")}
            maxLength={500}
            dir="auto"
            className="flex-1 rounded-full bg-[#12121C] border border-[#1F1F2A] focus:border-[#7C3AED] focus:outline-none px-3.5 py-2 text-sm text-white placeholder-[#52525B] transition-colors"
          />
          <button
            type="submit"
            disabled={sending || !text.trim()}
            data-testid="v2-match-chat-send"
            className="flex-shrink-0 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#3B82F6] text-white p-2 shadow-lg shadow-[#7C3AED]/30 hover:opacity-95 disabled:opacity-50 transition-opacity"
            aria-label={t("v2.dm.send")}
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </form>
    </section>
  );
}

function fmtChatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}


/**
 * Mod / Admin override card.
 * Surfaces directly inside the V2 match detail page so staff don't have to
 * hop into the V1 admin panel to resolve a disputed match or fix a result
 * a player reported. Calls POST /admin/matches/:match_id/override (already
 * gated by require_mod_or_admin on the backend).
 *
 * Pre-fills with confirmed scores if any, otherwise with the home player's
 * submission (defensive fallback to {0, 0} when neither side has submitted).
 */
function ModOverrideCard({ match, home, away, onApplied, t }) {
  const [homeScore, setHomeScore] = useState(() => {
    if (match.confirmed_score_home != null) return match.confirmed_score_home;
    if (match.home_submission?.score_home != null) return match.home_submission.score_home;
    return 0;
  });
  const [awayScore, setAwayScore] = useState(() => {
    if (match.confirmed_score_away != null) return match.confirmed_score_away;
    if (match.home_submission?.score_away != null) return match.home_submission.score_away;
    return 0;
  });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/matches/${match.id}/override`, {
        home_score: homeScore,
        away_score: awayScore,
      });
      toast.success(t("v2.match.mod.applied", { h: homeScore, a: awayScore }));
      setOpen(false);
      onApplied?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="rounded-2xl bg-[#7C3AED]/8 border border-[#7C3AED]/30 p-4 space-y-3"
      data-testid="v2-match-mod-override"
    >
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-[#A5B4FC]" />
        <div className="flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#A5B4FC]">{t("v2.match.mod.kicker")}</div>
          <h3 className="text-sm font-bold text-white">{t("v2.match.mod.title")}</h3>
        </div>
      </div>

      <p className="text-xs text-[#A1A1AA] leading-relaxed">{t("v2.match.mod.help")}</p>

      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="v2-match-mod-open"
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border border-[#7C3AED] text-[#A5B4FC] font-bold text-xs py-2.5 hover:bg-[#7C3AED] hover:text-white transition-colors"
        >
          <ShieldCheck className="w-4 h-4" />
          {match.admin_override ? t("v2.match.mod.cta.reoverride") : t("v2.match.mod.cta.open")}
        </button>
      ) : (
        <div className="space-y-3" data-testid="v2-match-mod-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#71717A] mb-1.5 truncate">
                {home?.username || "Home"}
              </div>
              <input
                type="number"
                min={0}
                max={50}
                value={homeScore}
                onChange={(e) => setHomeScore(Math.max(0, Math.min(50, parseInt(e.target.value || "0", 10))))}
                data-testid="v2-match-mod-home"
                className="w-full rounded-xl bg-[#0F0F18] border border-[#1F1F2A] focus:border-[#7C3AED] focus:outline-none px-4 py-3 text-2xl text-center font-bold text-white tabular-nums transition-colors"
              />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#71717A] mb-1.5 truncate">
                {away?.username || "Away"}
              </div>
              <input
                type="number"
                min={0}
                max={50}
                value={awayScore}
                onChange={(e) => setAwayScore(Math.max(0, Math.min(50, parseInt(e.target.value || "0", 10))))}
                data-testid="v2-match-mod-away"
                className="w-full rounded-xl bg-[#0F0F18] border border-[#1F1F2A] focus:border-[#7C3AED] focus:outline-none px-4 py-3 text-2xl text-center font-bold text-white tabular-nums transition-colors"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="v2-match-mod-cancel"
              className="rounded-2xl border border-[#1F1F2A] text-[#A1A1AA] font-bold text-xs py-2.5 hover:border-white hover:text-white transition-colors"
            >
              {t("v2.acc.del.cancel")}
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              data-testid="v2-match-mod-apply"
              className="rounded-2xl bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white font-bold text-xs py-2.5 hover:opacity-95 disabled:opacity-50 transition-opacity inline-flex items-center justify-center gap-1.5"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {t("v2.match.mod.apply")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}


/* ReschedulePanel — mutual-approval reschedule flow.
   Reuses the V1 endpoints POST /matches/:id/reschedule (propose / accept)
   and DELETE /matches/:id/reschedule (withdraw your own proposal). Matches
   the H2HV2 implementation 1:1, but accepts `userId` directly so callers in
   MatchDetailV2 don't need to wrap the auth user. */
function ReschedulePanel({ match, userId, onChanged, t }) {
  const proposals = match.reschedule_proposals || {};
  const myProposal = userId ? proposals[userId] : null;
  const otherEntries = Object.entries(proposals).filter(([uid]) => uid !== userId);
  const otherProposal = otherEntries.length ? otherEntries[0][1] : null;

  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(myProposal || match.scheduled_at || "");
  const [busy, setBusy] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  const submit = async () => {
    if (!date || date < today) {
      toast.error(t("v2.h2h.reschedule.errFuture"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/matches/${match.id}/reschedule`, { date });
      if (data.applied) toast.success(t("v2.h2h.reschedule.applied", { date }));
      else toast(t("v2.h2h.reschedule.proposed", { date }));
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      await api.delete(`/matches/${match.id}/reschedule`);
      toast.success(t("v2.h2h.reschedule.withdrawn"));
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const acceptOpponent = async () => {
    if (!otherProposal) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/matches/${match.id}/reschedule`, { date: otherProposal });
      if (data.applied) toast.success(t("v2.h2h.reschedule.applied", { date: otherProposal }));
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-4" data-testid={`v2-match-reschedule-${match.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <CalendarClock className="w-4 h-4 text-[#A5B4FC]" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-white">{t("v2.h2h.reschedule.cta")}</h3>
      </div>

      {otherProposal && otherProposal !== myProposal && (
        <div className="mb-3 rounded-xl bg-[#7C3AED]/10 border border-[#7C3AED]/40 px-3 py-2 flex items-center justify-between gap-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#A5B4FC] truncate">
            {t("v2.h2h.reschedule.oppProposed", { date: otherProposal })}
          </div>
          <button
            type="button"
            onClick={acceptOpponent}
            disabled={busy}
            data-testid={`v2-match-reschedule-accept-${match.id}`}
            className="flex-shrink-0 rounded-full bg-[#7C3AED] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 hover:bg-[#6D28D9] transition-colors disabled:opacity-50"
          >
            {t("v2.h2h.reschedule.accept")}
          </button>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">
          {t("v2.h2h.reschedule.scheduled")}: <span className="text-white">{match.scheduled_at || "—"}</span>
        </div>
        <div className="flex items-center gap-2">
          {myProposal && (
            <button
              type="button"
              onClick={withdraw}
              disabled={busy}
              data-testid={`v2-match-reschedule-withdraw-${match.id}`}
              className="rounded-full border border-[#1F1F2A] text-[#A1A1AA] hover:border-[#E11D48] hover:text-[#E11D48] text-[10px] font-bold uppercase tracking-widest px-3 py-1 transition-colors disabled:opacity-50"
            >
              {t("v2.h2h.reschedule.withdraw")}
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid={`v2-match-reschedule-toggle-${match.id}`}
            className="inline-flex items-center gap-1 rounded-full border border-[#7C3AED]/40 text-[#A5B4FC] hover:bg-[#7C3AED]/15 text-[10px] font-bold uppercase tracking-widest px-3 py-1 transition-colors"
          >
            <CalendarClock className="w-3 h-3" /> {t("v2.h2h.reschedule.cta")}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3 flex gap-2 items-center" data-testid={`v2-match-reschedule-form-${match.id}`}>
          <input
            type="date"
            min={today}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid={`v2-match-reschedule-date-${match.id}`}
            className="flex-1 rounded-xl bg-[#0F0F18] border border-[#1F1F2A] focus:border-[#7C3AED] focus:outline-none px-3 py-2 text-xs text-white transition-colors"
          />
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid={`v2-match-reschedule-submit-${match.id}`}
            className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white text-[10px] font-bold uppercase tracking-widest px-4 py-2 hover:opacity-95 transition-opacity disabled:opacity-50"
          >
            {t("v2.h2h.reschedule.send")}
          </button>
        </div>
      )}

      {myProposal && otherProposal !== myProposal && (
        <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#71717A]">
          {t("v2.h2h.reschedule.youProposed", { date: myProposal })}
        </div>
      )}
    </section>
  );
}
